// buildTimelineHierarchy.js

import { sortTimelineEntriesByStart } from './sortTimelineEntriesByStart.js';

/**
 * Orders timeline entries by hierarchy and assigns depth from parentKey.
 * Parents not in the set are treated as roots. Cycles collapse to depth 0.
 * Roots and siblings are ordered by start date ascending.
 *
 * @param {Array<object>} entries
 * @param {Record<string, string>} parentByChild
 * @returns {Array<object>}
 */
export function buildTimelineHierarchyEntries(entries = [], parentByChild = {}) {
  const list = Array.isArray(entries) ? entries.filter((entry) => entry?.key) : [];
  if (list.length === 0) return [];

  const byKey = new Map(list.map((entry) => [entry.key, entry]));
  const childrenByParent = new Map();
  const roots = [];

  list.forEach((entry) => {
    const parentKey = String(
      entry?.parentKey
      || parentByChild?.[entry.key]
      || '',
    ).trim();
    const resolvedParent = parentKey && byKey.has(parentKey) ? parentKey : '';
    if (!resolvedParent) {
      roots.push(entry);
      return;
    }
    if (!childrenByParent.has(resolvedParent)) {
      childrenByParent.set(resolvedParent, []);
    }
    childrenByParent.get(resolvedParent).push(entry);
  });

  const sortedRoots = sortTimelineEntriesByStart(roots);
  childrenByParent.forEach((children, parentKey) => {
    childrenByParent.set(parentKey, sortTimelineEntriesByStart(children));
  });

  const depthMemo = new Map();
  const ordered = [];
  const visiting = new Set();

  const visit = (entry, depth) => {
    if (!entry?.key) return;
    if (visiting.has(entry.key)) return;
    if (depthMemo.has(entry.key)) return;

    visiting.add(entry.key);
    const safeDepth = Math.min(6, Math.max(0, depth));
    depthMemo.set(entry.key, safeDepth);
    ordered.push({
      ...entry,
      parentKey: String(entry?.parentKey || parentByChild?.[entry.key] || '').trim() || null,
      depth: safeDepth,
    });

    const children = childrenByParent.get(entry.key) || [];
    children.forEach((child) => visit(child, safeDepth + 1));
    visiting.delete(entry.key);
  };

  sortedRoots.forEach((entry) => visit(entry, 0));

  // Orphans stuck in cycles / missed — append remaining (still start-sorted)
  const remaining = [];
  list.forEach((entry) => {
    if (!depthMemo.has(entry.key)) {
      remaining.push(entry);
    }
  });
  sortTimelineEntriesByStart(remaining).forEach((entry) => {
    ordered.push({
      ...entry,
      parentKey: String(entry?.parentKey || parentByChild?.[entry.key] || '').trim() || null,
      depth: 0,
    });
  });

  return ordered;
}
